'use client';
import { useRef, useState } from 'react';

import { HeycarImage } from '@core/Components/HeycarImage/HeycarImage';
import { InspectionReport } from '@core/Entities/Vehicle/VehicleInspectionReport';
import { camelSnakeKebabToSpaces } from '@core/Utils/Strings/CamelSnakeKebabToSpaces';
import { capitalCase } from '@core/Utils/Strings/CapitalCase';
import { Typography } from '@gds/Typography/Typography';
import { useImageCarousel } from '@shared-components/ImageCarousel/Hooks/UseImageCarousel';
import { Strings } from '@shared-components/ImageCarousel/ImageCarousel.entity';

import { GalleryMessages } from '../../Gallery/Gallery.messages';
import GalleryContainer from '../../Gallery/GalleryContainer';
import SlideshowGallery from '../../Gallery/SlideshowGallery/SlideshowGallery';

import { DescriptionList } from '../DescriptionList/DescriptionList';

import styles from './Faults.module.css';

export const FaultsClient = ({
  inspectionReport,
  imageCarouselStrings,
  galleryStrings,
  faultsStrings,
}: {
  inspectionReport: InspectionReport;
  imageCarouselStrings: Strings;
  galleryStrings: GalleryMessages;
  faultsStrings: Record<string, string>;
}) => {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentClickImgIndex, setCurrentClickImgIndex] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const carouselProps = useImageCarousel(carouselRef);
  const images = inspectionReport.faults.map(fault => ({
    url: fault.images[0],
    altText: `${faultsStrings.imgAltText}: ${fault.description}`,
    isRelative: false,
  }));

  const keysToDisplay = ['masterKey', 'spareKeys', 'inspectionNotes'];

  const reportKeys = Object.keys(inspectionReport).filter(key => keysToDisplay.includes(key));

  const handleCardClick = (index: number) => {
    setCurrentClickImgIndex(index);
    setIsGalleryOpen(true);
  };

  return (
    <>
      <div>
        <Typography variant="h6">{faultsStrings.general}</Typography>
        {reportKeys.map(key => (
          <DescriptionList
            key={key}
            label={capitalCase(camelSnakeKebabToSpaces(key))}
            value={inspectionReport[key as keyof InspectionReport] as string}
          />
        ))}
      </div>
      <Typography variant="h6">{faultsStrings.issues}</Typography>
      <div className={styles.wrpr}>
        {inspectionReport?.faults.map(({ images, part, description, type }, i) => (
          <section key={part + i} data-test-id={`fault-card-${i}`} className={styles.faultCard}>
            <a onClick={() => handleCardClick(i)} role="button">
              <HeycarImage
                src={images[0]}
                className={styles.img}
                alt={`${faultsStrings.imgAltText}: ${type}`}
                sizes="200px"
              />
              <Typography data-test-id={`fault-part-${i}`} Component="h5" variant="h6">
                {part}
              </Typography>
              <Typography data-test-id={`fault-type-${i}`} variant="subheading3">
                {type}
              </Typography>
              <Typography data-test-id={`fault-description-${i}`} variant="body4">
                {faultsStrings.suggested}: <br />
                {description}
              </Typography>
            </a>
          </section>
        ))}
      </div>
      {isGalleryOpen && (
        <GalleryContainer
          images={images}
          galleryStrings={galleryStrings}
          locale={'uk'}
          currentIndex={currentClickImgIndex || 0}
          step={2}
          setStep={() => null}
          onBackButtonClick={() => setIsGalleryOpen(false)}
        >
          <SlideshowGallery
            slideshowGalleryStrings={imageCarouselStrings}
            images={images}
            currentClickImgIndex={currentClickImgIndex || 0}
            carouselProps={carouselProps}
            locale={'uk'}
            isZoomEnabled
          />
        </GalleryContainer>
      )}
    </>
  );
};
